import React from "react"
import { Slider, Box, TextField, Stack } from "@mui/material"

export interface SliderProps {
    min: number
    max: number
    value: [number, number]
    onChange: (value: [number, number]) => void
    unit?: string
    colorScheme?: 'blue' | 'red' | 'green'
    readonly?: boolean // добавлено свойство readonly
}

const colorMap: Record<string, string> = {
    blue: '#2196f3',
    red: '#e53935',
    green: '#43a047',
}

const DoubleSlider: React.FC<SliderProps> = ({ min, max, value, onChange, unit, colorScheme = 'blue', readonly = false }) => {
    const step = Math.ceil((max - min) / 20) || 1
    const [inputValue, setInputValue] = React.useState<[number, number]>(value)

    React.useEffect(() => {
        setInputValue(value)
    }, [value])

    const handleChange = (_: any, newValue: number | number[], activeThumb?: number) => {
        if (!Array.isArray(newValue)) return

        let [left, right] = value
        if (activeThumb === 0) {
            left = Math.min(newValue[0], right - step)
        } else if (activeThumb === 1) {
            right = Math.max(newValue[1], left + step)
        }
        left = Math.max(min, Math.min(left, max - step))
        right = Math.min(max, Math.max(right, min + step))
        onChange([left, right])
    }

    const handleInputChange = (idx: 0 | 1) => (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = Number(e.target.value.replace(/\s/g, ''))
        if (isNaN(val)) val = inputValue[idx]
        let newValues: [number, number] = [...inputValue] as [number, number]
        newValues[idx] = val
        // Гарантируем порядок и границы
        if (idx === 0) {
            newValues[0] = Math.max(min, Math.min(val, value[1] - step))
        } else {
            newValues[1] = Math.min(max, Math.max(val, value[0] + step))
        }
        setInputValue(newValues)
    }

    const handleInputBlur = (idx: 0 | 1) => () => {
        // При потере фокуса обновляем значения бегунков
        let newValues: [number, number] = [...inputValue] as [number, number]
        if (idx === 0) {
            newValues[0] = Math.max(min, Math.min(inputValue[0], value[1] - step))
        } else {
            newValues[1] = Math.min(max, Math.max(inputValue[1], value[0] + step))
        }
        onChange(newValues)
    }

    const mainColor = colorMap[colorScheme] || colorMap.blue

    return (
        <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', mt: 4, padding: '0px 30px 0px 30px', position: 'relative' }}>
            <Slider
                value={value}
                min={min}
                max={max}
                step={step}
                onChange={(e, newValue, activeThumb) => handleChange(e, newValue, activeThumb)}
                valueLabelDisplay="on"
                marks={[
                    { value: min, label: <span style={{color:'#bdbdbd',fontSize:12, display:'inline-block', marginTop: 16}}>{min.toLocaleString()} {unit}</span> },
                    { value: max, label: <span style={{color:'#bdbdbd',fontSize:12, display:'inline-block', marginTop: 16}}>{max.toLocaleString()} {unit}</span> }
                ]}
                disabled={readonly}
                sx={{
                    color: mainColor,
                    height: 10,
                    padding: '30px 0 10px 0',
                    '& .MuiSlider-thumb': {
                        height: 24,
                        width: 24,
                        backgroundColor: '#fff',
                        border: `2px solid ${mainColor}`,
                        boxShadow: `0 2px 8px ${mainColor}33`,
                    },
                    '& .MuiSlider-valueLabel': {
                        backgroundColor: mainColor,
                        color: '#fff',
                        borderRadius: '4px',
                        fontWeight: 700,
                        fontSize: 14,
                        top: -26,
                        padding: '2px 8px',
                        boxShadow: `0 2px 8px ${mainColor}26`,
                    },
                    '& .MuiSlider-track': {
                        border: 'none',
                        backgroundColor: mainColor,
                        height: 10,
                        borderRadius: 6,
                    },
                    '& .MuiSlider-rail': {
                        opacity: 1,
                        backgroundColor: '#e3eaf2',
                        height: 10,
                        borderRadius: 6,
                    },
                    '& .MuiSlider-mark': {
                        display: 'none',
                    },
                }}
                valueLabelFormat={(v: number) => `${v.toLocaleString()}${unit ? ' ' + unit : ''}`}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 1, justifyContent: 'center' }}>
                <TextField
                    size="small"
                    value={inputValue[0]}
                    onChange={handleInputChange(0)}
                    onBlur={handleInputBlur(0)}
                    onKeyDown={e => { if (e.key === 'Enter') handleInputBlur(0)() }}
                    disabled={readonly}
                    inputProps={{ min, max: value[1] - step, type: 'number', style: { textAlign: 'center', height: 28, padding: '4px 8px' } }}
                />
                <TextField
                    size="small"
                    value={inputValue[1]}
                    onChange={handleInputChange(1)}
                    onBlur={handleInputBlur(1)}
                    onKeyDown={e => { if (e.key === 'Enter') handleInputBlur(1)() }}
                    disabled={readonly}
                    inputProps={{ min: value[0] + step, max, type: 'number', style: { textAlign: 'center', height: 28, padding: '4px 8px' } }}
                />
            </Stack>
            {/* Отсечки снизу */}
            <Box sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '35px',
                height: 0,
                pointerEvents: 'none',
            }}>
                {(() => {
                    const marks = [];
                    const decimals = step.toString().split('.')[1]?.length || 0;
                    let v = min + step;
                    while (v < max) {
                        const vFixed = Number(v.toFixed(decimals));
                        const percent = ((vFixed - min) / (max - min)) * 100;
                        marks.push(
                            <span key={vFixed} style={{
                                position: 'absolute',
                                left: `calc(${percent}% - 0.5px)`,
                                width: 1,
                                height: 8,
                                background: '#bdbdbd',
                                opacity: 0.7,
                                display: 'block',
                                borderRadius: 1,
                            }} />
                        );
                        v += step;
                    }
                    return marks;
                })()}
            </Box>
        </Box>
    )
}

export default DoubleSlider
